import React from 'react';
import {
  ShieldCheckIcon,
  ClockIcon,
  FingerPrintIcon,
  ExclamationTriangleIcon,
  SpeakerWaveIcon,
} from '../../icons';
import MatDashPanel from '../layout/MatDashPanel';
import { formatDateTime, formatDuration } from '../../utils/transcriptionMeta';

const AudioAssetPanel = ({
  audioAsset,
  audioAssetId,
  durationSeconds = 0,
  languageVersionCount = 1,
  sessionDeleted = false,
}) => {
  if (!audioAsset && !audioAssetId && !sessionDeleted) return null;

  const checksum =
    audioAsset?.checksum_sha256 ||
    audioAsset?.checksum ||
    audioAsset?.sha256 ||
    null;
  const retentionExpires =
    audioAsset?.retention_expires_at || audioAsset?.retention_expiry || null;
  const isImmutable = audioAsset?.is_immutable !== false;
  const assetDuration = audioAsset?.duration_seconds || durationSeconds;

  if (sessionDeleted) {
    return (
      <div className="md-alert md-alert-danger mb-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold">Original session deleted</h3>
            <p className="mt-1">
              The source recording for this session is no longer available (HTTP 410).
              Existing transcripts remain for reference, but no new language versions can
              be generated and audio playback may be unavailable.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MatDashPanel
      title="Original audio"
      icon={SpeakerWaveIcon}
      headerAction={
        isImmutable ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-matdash-success-subtle text-emerald-800 border border-emerald-200">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            Immutable source
          </span>
        ) : null
      }
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {audioAssetId && (
          <div>
            <dt className="text-matdash-muted">Audio asset ID</dt>
            <dd className="font-mono text-matdash-dark text-xs break-all">{audioAssetId}</dd>
          </div>
        )}
        {assetDuration > 0 && (
          <div>
            <dt className="text-matdash-muted flex items-center gap-1">
              <ClockIcon className="h-4 w-4" />
              Duration
            </dt>
            <dd className="text-matdash-dark">{formatDuration(assetDuration)}</dd>
          </div>
        )}
        {checksum && (
          <div className="sm:col-span-2">
            <dt className="text-matdash-muted flex items-center gap-1">
              <FingerPrintIcon className="h-4 w-4" />
              Checksum (SHA-256)
            </dt>
            <dd className="font-mono text-matdash-dark text-xs break-all">{checksum}</dd>
          </div>
        )}
        {retentionExpires && (
          <div>
            <dt className="text-matdash-muted">Retention expires</dt>
            <dd className="text-matdash-dark">{formatDateTime(retentionExpires)}</dd>
          </div>
        )}
        {languageVersionCount > 1 && (
          <div>
            <dt className="text-matdash-muted">Linked versions</dt>
            <dd className="text-matdash-dark">
              {languageVersionCount} language versions share this recording
            </dd>
          </div>
        )}
      </dl>
    </MatDashPanel>
  );
};

export default AudioAssetPanel;
